import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  (process.env.NODE_ENV === "development"
    ? "http://localhost:5000"
    : "https://upsc-backend-353e.onrender.com");

export default function PrelimsSeries({
  onExamModeChange,
}) {
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("latest");
  const [selectedTest, setSelectedTest] = useState(null);

  // Per-test runtime state
  const [answers, setAnswers] = useState({});        // { [qid:string]: string | string[] }
  const [reviewMarks, setReviewMarks] = useState({}); // { [qid:string]: boolean }
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const [submitted, setSubmitted] = useState(false);
  const [scoreDetails, setScoreDetails] = useState(null); // {finalScore,correctCount,wrongCount,attemptedCount}
  const [showReview, setShowReview] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false); // ⬅️ NEW

  const [attempts, setAttempts] = useState([]);
  const [testStarted, setTestStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [paused, setPaused] = useState(false);
  const [timeTaken, setTimeTaken] = useState(0);

  // fullscreen
  const [isFullScreen, setIsFullScreen] = useState(false);
  const examContainerRef = useRef(null);

  const token = JSON.parse(localStorage.getItem("user"))?.token;
  const STORAGE_KEY = selectedTest ? `state_test_${selectedTest._id}` : null;

  // ------- Helpers -------
  const qidOf = (q, idx) => (q?._id ? String(q._id) : `q-${idx}`);

  const arraysEqual = (a, b) => {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    const ar = [...a].sort();
    const br = [...b].sort();
    return ar.every((v, i) => v === br[i]);
  };

  const answeredCount = Object.values(answers).filter(
  (value) =>
    value !== undefined &&
    value !== null &&
    value !== ""
).length;

const filteredTests = [...tests]
  .filter((test) => {
    const matchesSearch =
      test.title
        ?.toLowerCase()
        .includes(search.toLowerCase()) ||
      test.section
        ?.toLowerCase()
        .includes(search.toLowerCase()) ||
      test.subject
        ?.toLowerCase()
        .includes(search.toLowerCase());

    if (!matchesSearch) return false;

    if (filter === "free") return test.isFree;

    if (filter === "premium") return !test.isFree;

    return true;
  })
  .sort((a, b) => {
    if (sort === "latest")
      return new Date(b.date) - new Date(a.date);

    if (sort === "oldest")
      return new Date(a.date) - new Date(b.date);

    return 0;
  });

  // ------- Fetch tests -------
  useEffect(() => {
    async function fetchTests() {
      try {
        const res = await axios.get(`${API_BASE}/api/user/tests/prelims`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTests(

Array.isArray(
res.data?.tests
)

?

res.data.tests

:

Array.isArray(
res.data
)

?

res.data

:

[]

);
      } catch (e) {

console.error(

"Failed to fetch tests",

e.response?.data ||

e.message

);

setTests([]);

}
    }
    fetchTests();
  }, [token]);

  // ------- Load persisted state for selected test -------
  useEffect(() => {
    if (!STORAGE_KEY) return;
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const state = JSON.parse(saved);
      setAnswers(state.answers || {});
      setReviewMarks(state.reviewMarks || {});
      setCurrentQuestionIndex(state.currentQuestionIndex || 0);
      setTimeLeft(state.timeLeft ?? (selectedTest?.timerMinutes * 60 || 0));
      setPaused(state.paused ?? false);
      setTestStarted(state.testStarted ?? false);
      setSubmitted(state.submitted ?? false);
      setScoreDetails(state.scoreDetails || null);
      setShowReview(state.showReview ?? false);
      setTimeTaken(state.timeTaken || 0);
    } else if (selectedTest) {
      setTimeLeft(selectedTest.timerMinutes * 60 || 0);
      setTimeTaken(0);
    }
  }, [selectedTest, STORAGE_KEY]);

  // ------- Persist state -------
  useEffect(() => {
    if (!STORAGE_KEY) return;
    const state = {
      answers,
      reviewMarks,
      currentQuestionIndex,
      timeLeft,
      paused,
      testStarted,
      submitted,
      scoreDetails,
      showReview,
      timeTaken,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [
    answers,
    reviewMarks,
    currentQuestionIndex,
    timeLeft,
    paused,
    testStarted,
    submitted,
    scoreDetails,
    showReview,
    timeTaken,
    STORAGE_KEY,
  ]);

  // ------- Timer -------
  const timerRef = useRef();
  useEffect(() => {
    if (testStarted && !paused && timeLeft > 0 && !submitted) {
      timerRef.current = setTimeout(() => {
        setTimeLeft((t) => t - 1);
        setTimeTaken((t) => t + 1);
      }, 1000);
    } else if (timeLeft <= 0 && testStarted && !submitted) {
      handleSubmit();
    }
    return () => clearTimeout(timerRef.current);
  }, [timeLeft, paused, testStarted, submitted]);

  // ------- Fullscreen -------
  useEffect(() => {
    const listener = () => {
      setIsFullScreen(document.fullscreenElement === examContainerRef.current);
    };
    document.addEventListener("fullscreenchange", listener);
    return () => document.removeEventListener("fullscreenchange", listener);
  }, []);

  function handleEnterFullScreen() {
    if (examContainerRef.current && !isFullScreen) {
      examContainerRef.current.requestFullscreen();
    }
  }
  function handleExitFullScreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
  }
  function handleToggleFullScreen() {
    isFullScreen ? handleExitFullScreen() : handleEnterFullScreen();
  }

  // ------- Attempts history -------
  async function fetchAttempts(testId) {
    try {
      const res = await axios.get(
        `${API_BASE}/api/user/tests/prelims/${testId}/attempts`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAttempts(res.data || []);
      return res.data || [];
    } catch {
      return [];
    }
  }

  // ------- Select test -------
 async function handleSelect(test) {

  // 🔒 Premium Protection
  if (!test.isFree && !isPremium) {
    alert("🔒 This is a premium test. Please upgrade to access.");
    return;
  }

  try {

    // ✅ FETCH FULL TEST WITH QUESTIONS
    const res = await axios.get(
      `${API_BASE}/api/user/tests/prelims/${test._id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const fullTest = res.data;

    const safeTest = {
      ...fullTest,
      questions: Array.isArray(fullTest.questions)
        ? fullTest.questions
        : [],
    };

    setSelectedTest(safeTest);

onExamModeChange?.(true);

const saved = localStorage.getItem(
  `state_test_${safeTest._id}`
);

if (saved) {
  return;
}

    setTestStarted(false);
    setPaused(false);
    setSubmitted(false);
    setScoreDetails(null);
    setShowReview(false);
    setShowInstructions(false);

    setAnswers({});
    setReviewMarks({});
    setCurrentQuestionIndex(0);

    setTimeLeft(fullTest.timerMinutes * 60 || 0);
    setTimeTaken(0);

    setAttempts([]);

    setAttempts([]);

try {
  await fetchAttempts(fullTest._id);
} catch {
  console.log("Attempts fetch skipped");
}

  } catch (err) {

    console.error("Failed to fetch full test", err);

    alert(
      err.response?.data?.message ||
      "Failed to fetch questions"
    );
  }
}

  // ▸ Show instructions first
  function startTest() {
    setShowInstructions(true);
  }

  // ▸ Actually begin test
  function beginTest() {
    setTestStarted(true);
    setShowInstructions(false);
    setPaused(false);
    if (timeLeft === 0 && selectedTest) {
      setTimeLeft(selectedTest.timerMinutes * 60);
    }
  }

  // ------- Answer & Review handlers (always string qid) -------
  function handleAnswerChange(qKey, value) {
    if (paused || submitted) return;
    const key = String(qKey);
    setAnswers((prev) => ({ ...prev, [key]: value }));
  }

  function toggleReviewMark(qKey) {
    if (paused || submitted) return;
    const key = String(qKey);
    setReviewMarks((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  // ------- Scoring -------
  function calculateScore(answerMap, questions = selectedTest?.questions || []) {
    let score = 0,
      correct = 0,
      wrong = 0,
      attempted = 0;

    questions.forEach((q, idx) => {
      const qid = qidOf(q, idx);
      const ans = answerMap[qid];

      if (ans !== undefined && ans !== null && ans !== "") {
        attempted++;
        const correctAnswer = q.correctAnswer;
        const isCorrect = Array.isArray(correctAnswer)
          ? Array.isArray(ans) && arraysEqual(ans, correctAnswer)
          : ans === correctAnswer;

        if (isCorrect) {
          correct++;
          score += 2;
        } else {
          wrong++;
          score -= 0.33;
        }
      }
    });

    return {
      finalScore: score,
      correctCount: correct,
      wrongCount: wrong,
      attemptedCount: attempted,
    };
  }

  async function handleSubmit() {
    if (submitted) return;
    setPaused(true);

    // Build clean answers map keyed by string qid
    const selAnswers = {};
    (selectedTest?.questions || []).forEach((q, idx) => {
      const qid = qidOf(q, idx);
      if (answers[qid] !== undefined && answers[qid] !== null && answers[qid] !== "") {
        selAnswers[qid] = answers[qid];
      }
    });

    // Calculate score and set UI
    const result = calculateScore(selAnswers, selectedTest?.questions || []);
    setScoreDetails(result);

    try {
      await axios.post(
        `${API_BASE}/api/user/tests/prelims/submit`,
        {
          testId: selectedTest._id,
          selectedAnswers: selAnswers,
          score: result.finalScore,
          timeTaken,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSubmitted(true);
      setPaused(false);
      if (STORAGE_KEY) localStorage.removeItem(STORAGE_KEY);
      await fetchAttempts(selectedTest._id);
      // =========================
// Live XP Event
// =========================

window.dispatchEvent(

  new CustomEvent(

    "xpGained",

    {

      detail: {

        xp:
          Math.max(
            0,
            Math.floor(
              result.finalScore * 10
            )
          ),

        message:
          "Prelims Test Completed",
      },
    }
  )
);
    } catch (e) {
      setPaused(false);
      alert(e.response?.data?.message || "Failed to submit test");
    }
  }

  function reattempt() {
    setTestStarted(false);
    setPaused(false);
    setSubmitted(false);
    setScoreDetails(null);
    setShowReview(false);
    setShowInstructions(false);
    setAnswers({});
    setReviewMarks({});
    setCurrentQuestionIndex(0);
    setTimeLeft(selectedTest.timerMinutes * 60 || 0);
    setTimeTaken(0);
    if (STORAGE_KEY) localStorage.removeItem(STORAGE_KEY);
  }

  // ------- Navigator button color logic (string qid) -------
  function getStatusClass(q, idx) {
    const qid = qidOf(q, idx);
    const isAttempted = !!answers[qid];
    const isReviewMarked = !!reviewMarks[qid];

    if (submitted && !showReview) return "bg-gray-300 dark:bg-gray-700 cursor-default";

if (isAttempted && isReviewMarked) return "bg-orange-400 dark:bg-orange-500";
if (isReviewMarked) return "bg-yellow-400 dark:bg-yellow-500";
if (isAttempted) return "bg-green-400 dark:bg-green-500";

return "bg-gray-300 dark:bg-gray-700";                                     
  }

  // ------- Renderers -------
  function renderQuestion(q, idx) {
    const qid = qidOf(q, idx);
    const userAnswer = answers[qid];

    return (
      <Card
        key={qid}
        className="
bg-white
dark:bg-gray-900
border
border-gray-200
dark:border-gray-700
shadow-xl
rounded-2xl
"
      >
        <CardContent>
         <div className="mb-4">
  <div className="font-semibold mb-2">
    Q{idx + 1}.
  </div>

  <div
  className="prose dark:prose-invert max-w-none text-gray-900 dark:text-gray-100"
    dangerouslySetInnerHTML={{
      __html: q.question,
    }}
  />
</div>

{q.images?.length > 0 && (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
    {q.images.map((img, idx) => (
      <img
        key={idx}
        src={img}
        alt={`question-${idx}`}
        className="
rounded-lg
border
border-gray-300
dark:border-gray-700
bg-white
dark:bg-gray-900
max-h-[400px]
object-contain
"
      />
    ))}
  </div>
)}

          {q.type === "statement" && q.statements?.length ? (
            <ol className="ml-6 list-decimal text-gray-800 dark:text-gray-200">
              {q.statements.map((st, i) => (
                <li key={i}>{st}</li>
              ))}
            </ol>
          ) : null}

          {q.options?.map((opt, i) => {
            const selected = userAnswer === opt;
            const correct = submitted
              ? Array.isArray(q.correctAnswer)
                ? q.correctAnswer.includes(opt)
                : q.correctAnswer === opt
              : false;
            const wrong = submitted && selected && !correct;

            return (
              <label
                key={i}
                className={`block p-2 my-2 rounded cursor-pointer transition-colors
                  ${
                    correct
                      ? "bg-green-200 dark:bg-green-800 text-green-900 dark:text-green-100"
                      : wrong
                      ? "bg-red-200 dark:bg-red-800 text-red-900 dark:text-red-100"
                      : selected
                      ? "bg-blue-100 dark:bg-blue-700 text-gray-900 dark:text-gray-100"
                      : "bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                  }`}
              >
                <input
  type="radio"
  name={`question-${qid}`}
  value={opt}
  checked={selected}
  onChange={() => handleAnswerChange(qid, opt)}
  disabled={paused || submitted}
  className="mr-2"
/>                  
                <>
  <span className="font-semibold mr-2">
    {["(a)", "(b)", "(c)", "(d)"][i]}
  </span>

  <span
    dangerouslySetInnerHTML={{
      __html: opt,
    }}
  />
</>
              </label>
            );
          })}

          <div className="flex flex-wrap gap-3 mt-6">

  <Button
  variant="outline"
  disabled={paused}
  onClick={() =>
    setCurrentQuestionIndex((i) =>
      Math.max(0, i - 1)
    )
  }
>
  Previous
</Button>

  <Button
  disabled={paused}
  onClick={() =>
    setCurrentQuestionIndex((i) =>
      Math.min(
        selectedTest.questions.length - 1,
        i + 1
      )
    )
  }
>
  Save & Next
</Button>

  <Button
  variant="secondary"
  disabled={paused}
  onClick={() => {
      toggleReviewMark(qid);

      setCurrentQuestionIndex((i) =>
        Math.min(
          selectedTest.questions.length - 1,
          i + 1
        )
      );
    }}
  >
    Review & Next
  </Button>

  <Button
  variant="destructive"
  disabled={paused}
  onClick={() =>
      handleAnswerChange(qid, "")
    }
  >
    Clear Response
  </Button>

</div>

          {submitted && showReview && (
            <div className="mt-2 text-sm text-gray-800 dark:text-gray-300">
              <b>Correct Answer: </b>
              {Array.isArray(q.correctAnswer)
                ? q.correctAnswer.join(", ")
                : q.correctAnswer}
              {q.explanation && (
  <div className="mt-2 p-2 bg-gray-100 dark:bg-gray-800 rounded text-sm">
    <div className="mt-2 p-3 bg-gray-100 dark:bg-gray-800 rounded">
  <h4 className="font-semibold mb-2">
    Explanation
  </h4>

  <div
  className="prose dark:prose-invert max-w-none text-gray-900 dark:text-gray-100"
    dangerouslySetInnerHTML={{
      __html: q.explanation,
    }}
  />
</div>
  </div>
)}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  function renderReviewQuestion(q, idx) {
    const qid = q._id?.toString() || `q-${idx}`;
    const userAnswer = answers[qid];
    const correctAnswer = Array.isArray(q.correctAnswer)
      ? q.correctAnswer
      : [q.correctAnswer];
    const userAnswerArr = userAnswer
      ? Array.isArray(userAnswer)
        ? userAnswer
        : [userAnswer]
      : [];

    const isAttempted = userAnswerArr.length > 0;
    const isCorrect =
      isAttempted &&
      (arraysEqual(userAnswerArr, correctAnswer) || userAnswer === correctAnswer);
    const isReviewMarked = reviewMarks[qid];

    // Final color logic
    let bgColor =
  "bg-red-50 dark:bg-red-900/20 border-red-400 dark:border-red-700";

if (!isAttempted)
  bgColor =
    "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-400 dark:border-yellow-700";

if (isCorrect)
  bgColor =
    "bg-green-50 dark:bg-green-900/20 border-green-400 dark:border-green-700";

if (isReviewMarked && !isAttempted)
  bgColor =
    "bg-gray-100 dark:bg-gray-800 border-gray-400 dark:border-gray-600";

if (isReviewMarked && isCorrect)
  bgColor =
    "bg-blue-50 dark:bg-blue-900/20 border-blue-500 dark:border-blue-700";

if (isReviewMarked && !isCorrect && isAttempted)
  bgColor =
    "bg-orange-50 dark:bg-orange-900/20 border-orange-400 dark:border-orange-700";

    return (
      <div
        key={qid}
        className={`
p-6
mb-8
rounded-2xl
shadow-lg
border-2
transition-all
duration-300
${bgColor}
`}
      >
        <div className="font-semibold mb-2">
          <div className="font-semibold mb-2">
  Q{idx + 1}
</div>

<div
  className="prose dark:prose-invert max-w-none text-gray-900 dark:text-gray-100"
  dangerouslySetInnerHTML={{
    __html: q.question,
  }}
/>
{q.images?.length > 0 && (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
    {q.images.map((img, idx) => (
      <img
        key={idx}
        src={img}
        alt=""
        className="rounded-lg border max-h-[400px] object-contain"
      />
    ))}
  </div>
)}
        </div>

        {q.type === "statement" && q.statements?.length ? (
          <ol className="ml-6 list-decimal mb-2">
            {q.statements.map((st, i) => (
              <li key={i}>{st}</li>
            ))}
          </ol>
        ) : null}

        {q.options?.length > 0 && (
          <ul className="ml-6 list-disc mb-2 text-gray-800 dark:text-gray-200">
            {q.options.map((opt, i) => {
              const selected = userAnswerArr.includes(opt);
              const correct = correctAnswer.includes(opt);
              return (
                <li
                  key={i}
                  className={`${selected ? "font-semibold underline" : ""} ${
                    correct ? "text-green-700 dark:text-green-300" : ""
                  }`}
                >
                  <span className="font-medium">
  {["(a)", "(b)", "(c)", "(d)"][i]}
</span>{" "}
<span
  dangerouslySetInnerHTML={{
    __html: opt,
  }}
/>
                </li>
              );
            })}
          </ul>
        )}

        <div className="mt-4 text-gray-900 dark:text-gray-100">
  <b>Your Answer:</b>

  {userAnswerArr.length ? (
    <div
      className="mt-2"
      dangerouslySetInnerHTML={{
        __html: userAnswerArr[0],
      }}
    />
  ) : (
    <span> No Answer</span>
  )}
</div>

<div className="mt-4 text-gray-900 dark:text-gray-100">
  <b>Correct Answer:</b>

  <div
    className="mt-2 text-green-700 font-medium"
    dangerouslySetInnerHTML={{
      __html: correctAnswer[0],
    }}
  />
</div>
        {q.explanation && (
  <div className="
mt-4
p-4
rounded-lg
bg-blue-50
dark:bg-blue-900/20
border
border-blue-200
dark:border-blue-700
text-gray-900
dark:text-gray-100
">
    <h4 className="font-semibold mb-2">
      Explanation
    </h4>

    <div
  className="prose dark:prose-invert max-w-none text-gray-900 dark:text-gray-100"
  dangerouslySetInnerHTML={{
    __html: q.explanation,
  }}
/>
  </div>
)}

        <div className="mt-3 text-sm italic text-gray-600 dark:text-gray-300">
          Status:{" "}
          {isCorrect ? "✅ Correct" : !isAttempted ? "⚠️ Unattempted" : "❌ Incorrect"}
          {isReviewMarked ? " 🔖 (Marked for Review)" : ""}
        </div>
      </div>
    );
  }

  function LegendBar() {
  return (
    <div className="flex flex-wrap gap-4 mb-6 text-sm text-gray-800 dark:text-gray-200">

      <div className="flex items-center gap-2">
        <div className="w-5 h-5 bg-green-400 dark:bg-green-500 rounded shadow-sm" />
        <span>✅ Correct</span>
      </div>

      <div className="flex items-center gap-2">
        <div className="w-5 h-5 bg-red-400 dark:bg-red-500 rounded shadow-sm" />
        <span>❌ Wrong</span>
      </div>

      <div className="flex items-center gap-2">
        <div className="w-5 h-5 bg-yellow-400 dark:bg-yellow-500 rounded shadow-sm" />
        <span>⚠️ Unattempted</span>
      </div>

      <div className="flex items-center gap-2">
        <div className="w-5 h-5 bg-blue-400 dark:bg-blue-500 rounded shadow-sm" />
        <span>🔵 Marked + Correct</span>
      </div>

      <div className="flex items-center gap-2">
        <div className="w-5 h-5 bg-orange-400 dark:bg-orange-500 rounded shadow-sm" />
        <span>🟠 Marked + Wrong</span>
      </div>

      <div className="flex items-center gap-2">
        <div className="w-5 h-5 bg-gray-400 dark:bg-gray-600 rounded shadow-sm" />
        <span>⚪ Marked + Unattempted</span>
      </div>

    </div>
  );
}

  function InstructionsPanel() {
    return (
      <div className="p-6 max-w-2xl mx-auto bg-white dark:bg-gray-900 
border dark:border-gray-700 rounded-xl shadow-2xl 
space-y-4 text-gray-900 dark:text-gray-100">
        <h2 className="text-2xl font-bold text-center mb-2 text-purple-700 dark:text-purple-400">📝 Test Instructions</h2>
        <div className="text-center text-sm text-gray-600 dark:text-gray-400 mb-2">
          {selectedTest?.title}
        </div>
        <ul className="list-disc pl-5 space-y-2 text-gray-800 dark:text-gray-200">
          <li>Total Questions: <b>{selectedTest?.questions?.length || 0}</b></li>
          <li>Total Time: <b>{selectedTest?.timerMinutes} minutes</b></li>
          <li>Marks: <b>+2</b> for correct, <b>-0.33</b> for incorrect.</li>
          <li>Each question has 4 options. Choose the most appropriate one.</li>
          <li>You can mark questions for review and revisit them later.</li>
          <li>Once submitted, answers cannot be changed.</li>
          <li>Fullscreen is recommended for better experience.</li>
        </ul>
        <div className="flex justify-center gap-4 mt-4">
          <Button onClick={beginTest} className="bg-green-600 hover:bg-green-700 text-white">
            ✅ Begin Test
          </Button>
          <Button onClick={() => setShowInstructions(false)} variant="outline">
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  function ResultScreen() {
    if (!scoreDetails) {
      return (
        <div className="text-center text-red-600 font-semibold text-lg">
          ⚠️ Score could not be calculated. Please try submitting again.
        </div>
      );
    }

    const accuracy =
  scoreDetails.attemptedCount > 0
    ? (
        (scoreDetails.correctCount /
          scoreDetails.attemptedCount) *
        100
      ).toFixed(1)
    : 0;

    return (
      <div className="max-w-5xl mx-auto bg-white dark:bg-gray-900 p-6 rounded shadow text-center border dark:border-gray-700">
        <div className="rounded-3xl p-8 mb-8 text-center bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-xl">
          <div className="mt-3 text-sm opacity-90">
  {scoreDetails.correctCount} Correct •{" "}
  {scoreDetails.wrongCount} Incorrect
</div>
  <div className="text-sm uppercase tracking-widest opacity-80">
    Test Completed
  </div>

  <div className="text-5xl font-bold mt-2">
    {scoreDetails.finalScore.toFixed(2)}
  </div>

  <div className="text-lg mt-2 opacity-90">
    Final Score
  </div>
</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="p-4 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 rounded-xl shadow text-gray-900 dark:text-gray-100">
  <div className="text-4xl font-bold text-green-700 dark:text-green-300">
              {scoreDetails.correctCount}
            </div>
            <div className="text-gray-700 dark:text-gray-300">
  Correct
</div>
          </div>
          <div className="p-4 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-xl shadow text-gray-900 dark:text-gray-100">
  <div className="text-4xl font-bold text-red-700 dark:text-red-300">
              {scoreDetails.wrongCount}
            </div>
            <div className="text-gray-700 dark:text-gray-300">
  Wrong
</div>
          </div>
          <div className="p-4 bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 rounded-xl shadow text-gray-900 dark:text-gray-100">
            <div className="text-4xl font-bold text-yellow-700 dark:text-yellow-300">
              {scoreDetails.attemptedCount}
            </div>
            <div className="text-gray-700 dark:text-gray-300">
  Attempted
</div>
          </div>
          <div className="rounded-2xl border border-blue-200 dark:border-blue-700 bg-white dark:bg-gray-800 p-5 shadow-lg text-gray-900 dark:text-gray-100">
  <div className="text-3xl font-bold">
    {accuracy}%
  </div>
  <div className="text-sm text-gray-600 dark:text-gray-400">
    Accuracy
  </div>
</div>
          <div className="p-4 bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700 rounded-xl shadow text-gray-900 dark:text-gray-100">
            <div className="text-4xl font-bold text-blue-700 dark:text-blue-300">
              {scoreDetails.finalScore.toFixed(2)}
            </div>
            <div className="text-gray-700 dark:text-gray-300">
  Score
</div>
          </div>
        </div>

        <div className="mt-6 rounded-xl border p-4 bg-gray-50 dark:bg-gray-800">
  {scoreDetails.attemptedCount < 20 ? (
  <div>
    📌 Attempt more questions for a meaningful performance analysis.
  </div>
) : accuracy >= 80 ? (
  <div>
    🔥 Excellent performance. You're exam ready.
  </div>
) : accuracy >= 60 ? (
  <div>
    📈 Good attempt. Focus on revision and weak areas.
  </div>
) : (
  <div>
    📚 More practice needed. Review explanations carefully.
  </div>
)}
</div>

        <div className="mb-4 font-medium space-y-2">
          <div className="text-lg font-semibold text-blue-600">
  Attempt #{
  attempts.length > 0
    ? attempts[0].attemptNumber ||
      attempts.length
    : 1
}
</div>
          <div className="text-lg font-medium">
  ⏱ Time Taken:{" "}
  {Math.floor(timeTaken / 3600)}h{" "}
  {Math.floor((timeTaken % 3600) / 60)}m{" "}
  {timeTaken % 60}s
</div>
        </div>
        {/* Previous Attempts */}

{attempts.length > 1 && (
  <div className="mt-8 mb-8">

    <h3 className="text-2xl font-bold mb-4 text-left">
      📊 Attempt History
    </h3>

    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">

      {attempts.map((attempt, index) => (

        <div
          key={attempt._id}
          className="
          bg-white
          dark:bg-gray-800
          border
          border-gray-200
          dark:border-gray-700
          rounded-2xl
          p-4
          shadow-lg
          text-left
          text-gray-900
          dark:text-gray-100
"
        >

          <div className="text-blue-600 dark:text-blue-400 font-bold text-lg">
            Attempt #{attempt.attemptNumber || (attempts.length - index)}
          </div>

          <div className="mt-2">
  <span className="font-medium">
    Score:
  </span>{" "}
  {attempt.score?.toFixed?.(2) ||
    attempt.score}
</div>

<div className="text-sm mt-2">
  ✅ Correct: {attempt.correctCount || 0}
</div>

<div className="text-sm">
  ❌ Wrong: {attempt.incorrectCount || 0}
</div>

<div className="text-sm">
  📝 Attempted: {attempt.attemptedCount || 0}
</div>

          <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {attempt.submittedAt
              ? new Date(
                  attempt.submittedAt
                ).toLocaleString()
              : "No date"}
          </div>

        </div>

      ))}

    </div>

  </div>
)}
        <div className="flex justify-center space-x-4">
          <Button onClick={() => setShowReview(true)}>
            Review Answers & Explanations
          </Button>
          <Button onClick={reattempt}>Reattempt</Button>
          <Button
            onClick={() => {
              setSelectedTest(null);
              if (onExamModeChange) {
  onExamModeChange(false);
}
              setTestStarted(false);
              setPaused(false);
              setSubmitted(false);
              setAnswers({});
              setReviewMarks({});
              setCurrentQuestionIndex(0);
              setScoreDetails(null);
              setShowReview(false);
              setShowInstructions(false);
              setAttempts([]);
              setTimeLeft(0);
              setTimeTaken(0);
            }}
          >
            Back to Tests
          </Button>
        </div>
      </div>
    );
  }

const user = JSON.parse(localStorage.getItem("user"));
const isPremium = user?.hasPaid;

const loadRazorpay = (src) =>
  new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = src;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const handleUpgradeClick = async () => {
  const res = await loadRazorpay(
    "https://checkout.razorpay.com/v1/checkout.js"
  );

  if (!res) {
    alert("Razorpay SDK failed to load");
    return;
  }

  try {
    const orderRes = await axios.post(
      `${API_BASE}/api/payments/create-order`,
      {
        amount: 399900,
        type: "testsPremium", // 🔥 DIFFERENT TYPE
      },
      {
        headers: { Authorization: `Bearer ${user.token}` },
      }
    );

    const { order } = orderRes.data;

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY,
      amount: order.amount,
      currency: order.currency,
      name: "UPSC Journey",
      description: "Test Series Premium",
      order_id: order.id,

      handler: async function (response) {
        try {
          await axios.post(
            `${API_BASE}/api/payments/verify-payment`,
            {
              razorpay_order_id: order.id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              type: "testsPremium",
            },
            {
              headers: { Authorization: `Bearer ${user.token}` },
            }
          );

          alert("✅ Payment Successful! Tests Unlocked");

          const updatedUser = { ...user, hasPaid: true };
          localStorage.setItem("user", JSON.stringify(updatedUser));

          window.location.reload();
        } catch {
          alert("Payment verification failed");
        }
      },

      prefill: {
        email: user.email,
        name: user.username,
      },

      theme: {
        color: "#2563eb",
      },
    };

    const paymentObject = new window.Razorpay(options);
    paymentObject.open();
  } catch (err) {
    alert("Payment failed. Try again.");
  }
};

  // ------- UI -------
  return (
    <div
      ref={examContainerRef}
      className={`p-6 ${
        isFullScreen
          ? "fixed inset-0 z-50 bg-white dark:bg-gray-900 flex flex-col"
          : "max-w-6xl mx-auto w-full"
      }`}
      style={{ boxSizing: "border-box" }}
    >
      <div className="flex justify-end mb-2">
        <Button
          variant="outline"
          onClick={handleToggleFullScreen}
          title={isFullScreen ? "Exit Fullscreen" : "Enter Fullscreen"}
        >
          {isFullScreen ? "Exit Fullscreen" : "Fullscreen"}
        </Button>
      </div>

      {!selectedTest ? (
  <>
    {/* Header */}
    <div className="mb-8">
      <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
        📚 Prelims Test Series
      </h1>

      <p className="mt-2 text-gray-600 dark:text-gray-400">
        Search, filter and attempt UPSC-standard mock tests.
      </p>
    </div>

    {/* Statistics */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">

      <div className="rounded-2xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-5 text-gray-900 dark:text-gray-100">
        <div className="text-3xl font-bold">
          {tests.length}
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Total Tests
        </div>
      </div>

      <div className="rounded-2xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-5 text-gray-900 dark:text-gray-100">
        <div className="text-3xl font-bold">
          {tests.filter(t => t.isFree).length}
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Free
        </div>
      </div>

      <div className="rounded-2xl bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 p-5 text-gray-900 dark:text-gray-100">
        <div className="text-3xl font-bold">
          {tests.filter(t => !t.isFree).length}
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Premium
        </div>
      </div>

      <div className="rounded-2xl bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 p-5 text-gray-900 dark:text-gray-100">
        <div className="text-3xl font-bold">
          {attempts.length}
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Attempts
        </div>
      </div>

    </div>

    {/* Search + Filter */}
    <div className="flex flex-col md:flex-row gap-4 mb-8 p-5 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm">

      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search tests..."
        className="flex-1 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-blue-500 focus:outline-none px-4 py-3"
      />

      <select
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:outline-none"
      >
        <option value="all">All</option>
        <option value="free">Free</option>
        <option value="premium">Premium</option>
      </select>

      <select
        value={sort}
        onChange={(e) => setSort(e.target.value)}
        className="rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:outline-none"
      >
        <option value="latest">Latest</option>
        <option value="oldest">Oldest</option>
      </select>

    </div>

    {/* Test Cards */}

    <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">

      {(filteredTests || []).map((test) => (

        <Card
          key={test._id}
          onClick={() => handleSelect(test)}
          className={`relative cursor-pointer rounded-2xl overflow-hidden transition-all duration-300
bg-white dark:bg-gray-800
border border-gray-200 dark:border-gray-700
shadow-sm

          ${
            test.isFree || isPremium
              ? "hover:shadow-2xl hover:-translate-y-1"
              : "opacity-80"
          }`}
        >

          {!test.isFree && !isPremium && (
            <div className="absolute inset-0 z-20 bg-gray-950/80 backdrop-blur-sm flex flex-col justify-center items-center">

              <div className="text-white text-3xl mb-3">
                🔒
              </div>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleUpgradeClick();
                }}
                className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 shadow-lg transition-all text-white"
              >
                Upgrade
              </button>

            </div>
          )}

          <CardContent className="p-6">

            <div className="flex justify-between items-start mb-3">

              <h2 className="font-bold text-xl text-gray-900 dark:text-white">
                {test.title}
              </h2>

              <span
                className={`text-xs px-2 py-1 rounded-full ${
                  test.isFree
                    ? "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300"
                    : "bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300"
                }`}
              >
                {test.isFree ? "FREE" : "PREMIUM"}
              </span>

            </div>

            <div className="space-y-2 text-sm text-gray-500 dark:text-gray-400">

              <div>
                📅 {new Date(test.date).toLocaleDateString()}
              </div>

              <div>
                ⏱ {test.timerMinutes} Minutes
              </div>

              <div>
                📝 {test.totalQuestions || 0} Questions
              </div>

              {test.section && (
                <div>
                  📚 {test.section}
                </div>
              )}

            </div>

          </CardContent>

        </Card>

      ))}

    </div>

    {filteredTests.length === 0 && (
      <div className="text-center py-16 text-gray-500 dark:text-gray-400">
        No tests found.
      </div>
    )}

  </>
) : !testStarted ? (
  <div className="flex items-center justify-center min-h-[70vh]">

    {showInstructions ? (
      <InstructionsPanel />
    ) : (
      <div className="w-full max-w-2xl bg-white dark:bg-gray-900 
      border dark:border-gray-700 rounded-xl shadow-2xl 
      p-8 space-y-4 text-center">

        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
          {selectedTest.title}
        </h2>

        <div className="text-gray-600 dark:text-gray-300">
          {selectedTest.date
            ? new Date(selectedTest.date).toLocaleDateString()
            : "No Date"}
        </div>

        <div className="text-gray-600 dark:text-gray-300">
          Time Allowed: {selectedTest.timerMinutes} minutes
        </div>

        <div className="flex justify-center gap-4 mt-4">
          <Button
  onClick={
    paused
      ? () => setPaused(false)
      : startTest
  }
>
  {paused ? "▶ Resume Test" : "Start Test"}
</Button>
          <Button
  onClick={() => {
    setSelectedTest(null);

    if (onExamModeChange) {
      onExamModeChange(false);
    }
  }}
>
  Back
</Button>
        </div>

      </div>
    )}
  </div>
) : (
  <div className={`flex flex-col ${isFullScreen ? "flex-1 overflow-auto" : ""}`}>

    {submitted ? (
      showReview ? (
        <>
          <Button
            className="mb-4"
            onClick={() => setShowReview(false)}
          >
            Back To Result
          </Button>

          <LegendBar />

          {selectedTest?.questions?.map((q, i) =>
            renderReviewQuestion(q, i)
          )}
        </>
      ) : (
        <ResultScreen />
      )
    ) : (

      <div className="flex flex-col gap-4">

        {/* ── TOP PANEL: Timer + Palette ── */}
        <div className="sticky top-0 z-20 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-md p-3">

          {/* Row 1: Timer + stats + Pause + Submit */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-2">

            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">⏱</span>
              <span className={`text-xl font-bold ${timeLeft <= 60 ? "text-red-500 animate-pulse" : "text-blue-600"}`}>
                {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, "0")}
              </span>
              <div className="w-24 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-600 transition-all"
                  style={{ width: `${(timeLeft / (selectedTest.timerMinutes * 60)) * 100}%` }}
                />
              </div>
            </div>

            <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
              <span>✅ {answeredCount}</span>
              <span>🔖 {Object.values(reviewMarks).filter(Boolean).length}</span>
              <span>⚪ {selectedTest.questions.length - answeredCount}</span>
              <span className="text-gray-400 dark:text-gray-500">
                Q {currentQuestionIndex + 1}/{selectedTest.questions.length}
              </span>
            </div>

            <div className="flex gap-2 flex-wrap">

  <Button
    variant="outline"
    size="sm"
    onClick={() => setPaused(!paused)}
  >
    {paused ? "▶ Resume" : "⏸ Pause"}
  </Button>

  {paused && (
    <>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => {
          setSelectedTest(null);

          if (onExamModeChange) {
            onExamModeChange(false);
          }
        }}
      >
        📚 Back To Tests
      </Button>

      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          navigate("/dashboard");
        }}
      >
        🏠 Dashboard
      </Button>
    </>
  )}

  <Button
    size="sm"
    onClick={handleSubmit}
    disabled={paused || answeredCount === 0}
  >
    Submit Test
  </Button>

</div>

          </div>

          {/* Row 2: Question Palette */}
          <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto py-1">
            {selectedTest?.questions?.map((q, i) => (
              <button
  key={qidOf(q, i)}
  type="button"
  disabled={paused}
  onClick={() => setCurrentQuestionIndex(i)}
                className={`h-8 w-8 rounded-lg text-xs font-semibold transition-all hover:scale-105 ${
                  currentQuestionIndex === i
                    ? "bg-blue-600 text-white"
                    : getStatusClass(q, i)
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>

          {/* Row 3: Legend + warning */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-500 dark:text-gray-400">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-400 inline-block" /> Answered</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-yellow-400 inline-block" /> Review</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-400 inline-block" /> Ans + Review</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-gray-300 dark:bg-gray-600 inline-block" /> Unanswered</span>
            {answeredCount === 0 && (
              <span className="text-red-500 ml-2">⚠ Answer at least one question to submit.</span>
            )}
          </div>

        </div>

        {/* ── QUESTION ── */}
        <div>
          {paused && (
  <div className="
    mb-4
    rounded-xl
    border
    border-yellow-300
    bg-yellow-50
    p-4
    text-center
  ">
    <h3 className="font-bold text-yellow-700">
      ⏸ Test Paused
    </h3>

    <p className="text-sm text-gray-700 mt-1">
      Timer is stopped. Navigation and answer editing are locked.
      Click Resume to continue.
    </p>
  </div>
)}
          {selectedTest?.questions?.length > 0 ? (
            renderQuestion(
              selectedTest.questions[currentQuestionIndex],
              currentQuestionIndex
            )
          ) : (
            <p>No questions available</p>
          )}
        </div>

      </div>

    )}

  </div>
)}
    </div>
  );
}
